import { sitesQueryOptions } from "@/api/Patients";
import { useUserProfile } from "@/api/Users";
import { deducedError } from "@/Utils/ErrorUtils";
import { CHAT_ENVIRONMENT } from "@/Utils/EnvUtils";
import { ActionCardLink } from "@/components/ActionCard/ActionCard";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { Heading } from "@medoma/components/Heading/Heading";
import { Loading } from "@medoma/components/Loading/Loading";
import { NotificationCircle } from "@medoma/components/NotificationCircle/NotificationCircle";
import { useQuery } from "@tanstack/react-query";
import { Trans, useLingui } from "@lingui/react/macro";
import { getEmployeeRoomName } from "@medoma/chat/roomAlias";
import { useUnreadNotificationCounts } from "@medoma/chat/useUnreadNotificationCounts";
import { Navigate } from "react-router";
import styles from "./Chat.module.scss";

const SiteChatLink = ({
  site,
}: {
  site: { id: string; displayName: string };
}) => {
  const { t } = useLingui();
  const { employeeNumber, organization } = useUserProfile();
  const roomName = getEmployeeRoomName({
    employeeId: employeeNumber,
    env: CHAT_ENVIRONMENT,
    organization,
    site: site.id,
  });
  const unreadCount = useUnreadNotificationCounts({ roomName });
  const hasUnread = unreadCount !== undefined && unreadCount > 0;
  const unreadLabel = t`Olästa meddelanden`;
  const ariaLabel = hasUnread
    ? `${site.displayName}, ${unreadLabel}`
    : site.displayName;

  return (
    <ActionCardLink
      title={site.displayName}
      to={`${site.id}/center`}
      aria-label={ariaLabel}
      icon={
        hasUnread ? (
          <NotificationCircle number={unreadCount} isVisible={true} />
        ) : undefined
      }
    />
  );
};

const Chat = () => {
  const { t } = useLingui();

  const {
    data: sites,
    isPending: isSitesPending,
    isError: isSitesError,
    error: sitesError,
  } = useQuery(sitesQueryOptions);

  if (isSitesPending) {
    return <Loading message={t`Hämtar enheter`} />;
  }

  if (isSitesError) {
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta enheter.`} ${deducedError(sitesError)}`}
      />
    );
  }

  if (sites.length === 0) {
    return (
      <ErrorMessage
        message={t`Du har inte åtkomst till någon enhet. Kontakta support.`}
      />
    );
  }

  const singleSite = sites.length === 1 ? sites[0] : undefined;

  if (singleSite) {
    return <Navigate to={`${singleSite.id}/center`} replace />;
  }

  return (
    <div className={styles.container}>
      <Heading level="h1" id="chat-rooms-list-label">
        <Trans>Chatt</Trans>
      </Heading>

      <ul className={styles.list} aria-labelledby="chat-rooms-list-label">
        {sites.map((site) => (
          <li key={site.id}>
            <SiteChatLink site={site} />
          </li>
        ))}
      </ul>
    </div>
  );
};

export { Chat };
